/**
 * SurebetViewActiveFilterChips - Removable chips for active arbitrage view filters.
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSurebet } from '../context/SurebetContext';
import { useTranslation } from '../../i18n/hooks';
import { getMarginTypeName } from '../utils/surebetUtils';
import { normalizeSearchText, TIME_WINDOW_VALUES } from '../viewFilters/surebetViewFilters';
import { buildGroupChips } from '../viewFilters/surebetViewActiveChips';
import '../../bestOdds/components/BestOddsActiveFilterChips.css';

const TIME_WINDOW_LABEL_KEYS = {
  today: 'surebets.viewFilters.timeWindowToday',
  next24h: 'surebets.viewFilters.timeWindowNext24h',
  next48h: 'surebets.viewFilters.timeWindowNext48h'
};

const SurebetViewActiveFilterChips = ({ className = '' }) => {
  const { t, language } = useTranslation();
  const {
    viewFilters,
    hasActiveViewFilters,
    setMinMargin,
    setSearchQuery,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetViewFilters
  } = useSurebet();

  const chips = useMemo(() => {
    const next = [];
    const minMargin = Number(viewFilters?.minMargin);
    const searchQuery = viewFilters?.searchQuery || '';
    const timeWindow = viewFilters?.timeWindow;
    const selectedChampionships = viewFilters?.selectedChampionships;
    const selectedMarketKeys = viewFilters?.selectedMarketKeys;

    if (Number.isFinite(minMargin) && minMargin > 0) {
      next.push({
        id: 'minMargin',
        type: 'minMargin',
        label: t('surebets.viewFilters.marginChip', { value: minMargin })
      });
    }

    if (normalizeSearchText(searchQuery)) {
      next.push({
        id: 'search',
        type: 'search',
        label: `“${searchQuery.trim()}”`
      });
    }

    if (timeWindow && timeWindow !== 'all' && TIME_WINDOW_VALUES.includes(timeWindow)) {
      const windowLabel = t(TIME_WINDOW_LABEL_KEYS[timeWindow] || 'surebets.viewFilters.timeWindowLabel');
      next.push({
        id: 'timeWindow',
        type: 'timeWindow',
        label: `${t('surebets.viewFilters.timeWindowLabel')}: ${windowLabel}`
      });
    }

    next.push(...buildGroupChips({
      selected: selectedChampionships,
      noneId: 'championships-none',
      noneType: 'championships-none',
      noneLabel: t('surebets.viewFilters.championshipsNone'),
      itemType: 'championship',
      idPrefix: 'championship-',
      getItemId: (name) => name,
      getItemLabel: (name) => name,
      summaryId: 'championships-summary',
      summaryType: 'championships-summary',
      summaryLabel: `${t('surebets.viewFilters.championshipsLabel')}: ${selectedChampionships?.length || 0}`
    }));

    next.push(...buildGroupChips({
      selected: selectedMarketKeys,
      noneId: 'markets-none',
      noneType: 'markets-none',
      noneLabel: t('surebets.viewFilters.marketsNone'),
      itemType: 'market',
      idPrefix: 'market-',
      getItemId: (key) => key,
      getItemLabel: (key) => getMarginTypeName(key, language),
      summaryId: 'markets-summary',
      summaryType: 'markets-summary',
      summaryLabel: `${t('surebets.viewFilters.marketsLabel')}: ${selectedMarketKeys?.length || 0}`
    }));

    return next;
  }, [viewFilters, language, t]);

  const handleRemoveChip = useCallback((chip) => {
    if (chip.type === 'minMargin') {
      setMinMargin(0);
      return;
    }

    if (chip.type === 'search') {
      setSearchQuery('');
      return;
    }

    if (chip.type === 'timeWindow') {
      setTimeWindow('all');
      return;
    }

    if (chip.type === 'championships-none' || chip.type === 'championships-summary') {
      setSelectedChampionships(null);
      return;
    }

    if (chip.type === 'championship') {
      const current = viewFilters?.selectedChampionships;
      if (!Array.isArray(current)) {
        return;
      }
      setSelectedChampionships(current.filter((name) => name !== chip.item));
      return;
    }

    if (chip.type === 'markets-none' || chip.type === 'markets-summary') {
      setSelectedMarketKeys(null);
      return;
    }

    if (chip.type === 'market') {
      const current = viewFilters?.selectedMarketKeys;
      if (!Array.isArray(current)) {
        return;
      }
      setSelectedMarketKeys(current.filter((key) => key !== chip.item));
    }
  }, [
    viewFilters?.selectedChampionships,
    viewFilters?.selectedMarketKeys,
    setMinMargin,
    setSearchQuery,
    setTimeWindow,
    setSelectedChampionships,
    setSelectedMarketKeys
  ]);

  const handleClearAll = useCallback(() => {
    resetViewFilters();
  }, [resetViewFilters]);

  if (!hasActiveViewFilters || chips.length === 0) {
    return null;
  }

  return (
    <div
      className={`best-odds-active-chips ${className}`.trim()}
      role="list"
      aria-label={t('surebets.viewFilters.activeFilters')}
    >
      <span className="best-odds-active-chips__heading">
        {t('surebets.viewFilters.activeFilters')}
      </span>

      <div className="best-odds-active-chips__row">
        {chips.map((chip) => (
          <span key={chip.id} className="best-odds-active-chips__chip" role="listitem">
            <span className="best-odds-active-chips__chip-label" title={chip.label}>
              {chip.label}
            </span>
            <button
              type="button"
              className="best-odds-active-chips__chip-remove"
              onClick={() => handleRemoveChip(chip)}
              aria-label={t('surebets.viewFilters.removeFilterAriaLabel', { name: chip.label })}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <button
        type="button"
        className="best-odds-active-chips__clear-all"
        onClick={handleClearAll}
        aria-label={t('surebets.viewFilters.clearAllAriaLabel')}
      >
        {t('surebets.viewFilters.clearAll')}
      </button>
    </div>
  );
};

SurebetViewActiveFilterChips.propTypes = {
  className: PropTypes.string
};

export default SurebetViewActiveFilterChips;
